/**
 * Available now / Online presence helpers.
 * Presence payloads never include IP or street address.
 *
 * Source of truth is the listing publicData.availableNow flag written when
 * a listener turns Available on or off. Heartbeats are optional overlay.
 * Defaults OFF: a fresh login or logout clears it, and the listener turns
 * it on by hand. Tab close and idle do not clear it (see safety auto-off
 * option in the Oct 3 2026 report).
 */

export const LISTING_AVAILABLE_NOW_KEY = 'availableNow';

/**
 * Sticky Available saved on the Listening profile.
 *
 * @param {Object} [listing]
 * @returns {boolean}
 */
// Oct 29 2026 (Sam lock): Available = calendar hours only. No switch / manual overrides.
export const listingHasAvailableNow = (listing, nowMs = Date.now()) => {
  const plan = listing?.attributes?.availabilityPlan;
  if (plan) return isPlanOpenAt(plan, nowMs);
  // No plan yet: not Available (earners set hours in the calendar).
  return false;
};

// Keep in sync with server/api-util/presenceStore.js IDLE_MS.
// Sitting still on an open tab is not idle. Only a long background gap expires.
export const PRESENCE_IDLE_MS = 15 * 60 * 1000;
export const PRESENCE_HEARTBEAT_MS = 60 * 1000;
export const PRESENCE_POLL_MS = 60 * 1000;

// Oct 29 2026 (Sam lock): "If it says Available they have to be Available."
// Online here = REAL Available Now (live presence). The sticky listing flag alone
// is "Texts you" / Book later, never Available Now, Start now or free Talk Now.
export const isListingOnline = (listingId, presenceMap = {}, listing) => {
  if (!listingId) {
    return false;
  }
  return presenceMap[listingId] === true;
};

/**
 * Start now is allowed when the listener turned Available on (persisted)
 * or a live heartbeat says they are online.
 */
export const canStartNowWithListener = ({
  isTalkSession,
  listenerOnline,
  isOwnListing,
  isClosed,
  startNowPaused,
  sessionBusy,
} = {}) =>
  !!isTalkSession &&
  !!listenerOnline &&
  !isOwnListing &&
  !isClosed &&
  !startNowPaused &&
  !sessionBusy;

/**
 * listingId → Start now is hidden (accepted booking starts within 25 minutes).
 *
 * @param {Object} [payload]
 * @returns {Object<string, boolean>}
 */
export const presencePausedMapFromResponse = payload => {
  const listings = payload?.data?.listings || payload?.listings || [];
  return listings.reduce((acc, item) => {
    if (item?.listingId) {
      acc[item.listingId] = !!item.startNowPaused;
    }
    return acc;
  }, {});
};

/**
 * listingId → listener is in a live conversation.
 *
 * @param {Object} [payload]
 * @returns {Object<string, boolean>}
 */
export const presenceBusyMapFromResponse = payload => {
  const listings = payload?.data?.listings || payload?.listings || [];
  return listings.reduce((acc, item) => {
    if (item?.listingId) {
      acc[item.listingId] = !!item.sessionBusy;
    }
    return acc;
  }, {});
};

/**
 * listingId → earliest next-available epoch ms (session end or pause).
 *
 * @param {Object} [payload]
 * @returns {Object<string, number>}
 */
export const presenceNextAvailableMapFromResponse = payload => {
  const listings = payload?.data?.listings || payload?.listings || [];
  return listings.reduce((acc, item) => {
    const at = Number(item?.nextAvailableAt || item?.sessionEndsAt);
    if (item?.listingId && Number.isFinite(at) && at > 0) {
      acc[item.listingId] = at;
    }
    return acc;
  }, {});
};

export const mergePausedMaps = (byIds = {}, allOnline = {}) => {
  const merged = { ...byIds };
  Object.keys(allOnline || {}).forEach(listingId => {
    if (allOnline[listingId]) {
      merged[listingId] = true;
    }
  });
  return merged;
};

export const sortAvailableNowFirst = (listings = [], presenceMap = {}) => {
  return [...listings].sort((a, b) => {
    const ao = isListingOnline(a?.id?.uuid, presenceMap, a) ? 0 : 1;
    const bo = isListingOnline(b?.id?.uuid, presenceMap, b) ? 0 : 1;
    return ao - bo;
  });
};

/**
 * listingId -> live heartbeat (Available Now). Sticky Available without a
 * recent heartbeat comes back live:false and shows as Busy (Sam, Oct 26 2026).
 */
export const presenceLiveMapFromResponse = payload => {
  const listings = payload?.data?.listings || payload?.listings || [];
  return listings.reduce((acc, item) => {
    if (item?.listingId) {
      acc[item.listingId] = item.live === true || (!!item.sessionBusy && !!item.online);
    }
    return acc;
  }, {});
};

export const presenceMapFromResponse = payload => {
  const listings = payload?.data?.listings || payload?.listings || [];
  return listings.reduce((acc, item) => {
    if (item?.listingId) {
      // Live only (fresh heartbeat / check-in Yes) or in a live session.
      acc[item.listingId] = !!item.online && (item.live === true || !!item.sessionBusy);
    }
    return acc;
  }, {});
};

/**
 * Overlay the unfiltered online list on a by-id map. A true from the
 * public roster wins so a stale ?ids= offline cannot hide Start now.
 */
export const mergePresenceMaps = (byIds = {}, allOnline = {}) => {
  const merged = { ...byIds };
  Object.keys(allOnline || {}).forEach(listingId => {
    if (allOnline[listingId]) {
      merged[listingId] = true;
    }
  });
  return merged;
};

export const isPresenceRateLimited = error =>
  error?.code === 'rate_limited' ||
  error?.status === 429 ||
  error?.sharetribeStatus === 429 ||
  /status code 429/i.test(`${error?.error || ''} ${error?.message || ''}`);

export const presenceRetryAfterMs = error => {
  const seconds = Number(error?.retryAfter);
  return (Number.isFinite(seconds) && seconds > 0 ? seconds : 30) * 1000;
};

export const uniqueListingIds = (ids = []) => [
  ...new Set((ids || []).filter(id => typeof id === 'string' && id)),
];

/**
 * True only after the tab has been in the background longer than the stale
 * window. An open Dashboard with no mouse movement is still available.
 *
 * @param {Object} [opts]
 * @param {string} [opts.visibilityState]
 * @param {number} [opts.lastVisibleAt]
 * @param {number} [opts.now]
 * @param {number} [opts.staleMs]
 * @returns {boolean}
 */
export const isPresenceSessionIdle = ({
  visibilityState = 'visible',
  lastVisibleAt,
  now = Date.now(),
  staleMs = PRESENCE_IDLE_MS,
} = {}) =>
  visibilityState === 'hidden' && Number.isFinite(lastVisibleAt) && now - lastVisibleAt > staleMs;

/**
 * Heartbeat tick: keep intended + currently-online listings alive.
 * Sticky Available is listing-persisted — never idle-off. A hidden tab
 * just skips the heartbeat.
 */
export const presenceTickPlan = ({
  idle,
  serverOnlineIds = [],
  intendedIds = [],
  offlineIds = [],
} = {}) => {
  const skip = new Set(offlineIds || []);
  const listingIds = uniqueListingIds([
    ...intendedIds,
    ...(serverOnlineIds || []).filter(id => !skip.has(id)),
  ]);
  return {
    action: 'heartbeat',
    listingIds: idle ? [] : listingIds,
  };
};

export const isPresenceOffError = error => error?.code === 'presence_off' || error?.status === 409;

/** Server turned Available off after 2 hours away. Never auto-restore it. */
export const isPresenceAutoOffError = error => error?.code === 'auto_off';

export const shouldRestorePresenceAfterHeartbeat = ({ intended, idle, error } = {}) =>
  !!intended && !idle && !isPresenceAutoOffError(error) && isPresenceOffError(error);

/**
 * After Available now succeeds, ignore a later offline poll until the hold
 * expires. Covers stale public presence reads and a heartbeat that started
 * while idle, then finished after the click.
 */
export const shouldHoldAvailableNow = ({ optimistic, online, holdUntil, now = Date.now() } = {}) =>
  optimistic === true && online !== true && Number.isFinite(holdUntil) && now < holdUntil;

export const shouldApplyPresenceResponse = (responseToken, currentToken) =>
  responseToken === currentToken;

export const PRESENCE_ID_LIMIT = 50;
const PRESENCE_UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Unique UUIDs, capped at 50, for ?ids= presence queries. */
export const capPresenceIds = listingIds => {
  const out = [];
  (listingIds || []).forEach(raw => {
    const id = typeof raw === 'string' ? raw : raw?.uuid || '';
    if (PRESENCE_UUID_RE.test(id) && !out.includes(id) && out.length < PRESENCE_ID_LIMIT) {
      out.push(id);
    }
  });
  return out;
};

// Oct 29 2026 (Sam lock): Available follows the listening calendar only.
// Outside scheduled hours they are Unavailable until hours are changed.
const SCHED_DAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
const schedMinutes = v => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(v || ''));
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};
export const isPlanOpenAt = (plan, nowMs = Date.now()) => {
  if (!plan || plan.type === 'availability-plan/day' || !Array.isArray(plan.entries)) {
    return false;
  }
  let parts;
  try {
    parts = new Intl.DateTimeFormat('en-US', {
      timeZone: plan.timezone || 'America/New_York',
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(new Date(nowMs));
  } catch (e) {
    return true;
  }
  const read = t => parts.find(p => p.type === t)?.value;
  const day = String(read('weekday') || '').slice(0, 3).toLowerCase();
  const mins = Number(read('hour')) * 60 + Number(read('minute'));
  return plan.entries.some(e => {
    if ((e?.seats || 0) < 1 || e.dayOfWeek !== day) return false;
    const s = schedMinutes(e.startTime);
    const rawEnd = schedMinutes(e.endTime);
    if (s == null || rawEnd == null) return false;
    const end = rawEnd === 0 ? 24 * 60 : rawEnd;
    return s <= mins && mins < end;
  });
};
export const listingManualOnUntil = listing =>
  Number(listing?.attributes?.publicData?.availableManualUntil) || 0;
export const listingWithinAvailableHours = (listing, nowMs = Date.now()) =>
  listingHasAvailableNow(listing, nowMs);
